import React, { useCallback, useEffect } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { track } from 'apis/amethyst';
import { evSephoraBeautyInsider, SephoraInteraction } from 'events/sephora';
import { SEPHORA_LEARN_MORE_LINK, SEPHORA_PAGE_TYPE } from 'constants/sephora';
import { setFederatedLoginModalVisibility } from 'actions/headerfooter';
import { unlinkSephoraAccount } from 'actions/sephora';
import { selectPathname } from 'selectors/router';
import { selectIsLoadingLoyalty, selectLoyaltyEmail } from 'selectors/sephora';
import useMartyContext from 'hooks/useMartyContext';
import useSephoraContext from 'hooks/useSephoraContext';
import SephoraBeautyInsiderIcon from 'components/sephora/BeautyInsiderIcon';
import SephoraUserIcon from 'components/sephora/SephoraBIIcon';
import BeautyInsiderFormHeader from 'components/sephora/BeautyInsiderFormHeader';
import BeautyInsiderForm from 'components/sephora/BeautyInsiderForm';
import { SmallLoader } from 'components/Loader';
import MartyLink from 'components/common/MartyLink';

import css from 'styles/components/sephora/beautyInsider.scss';

export interface BeautyInsiderProps {
  isLoading?: boolean;
  isFederatedLogin?: boolean;
  isZapposCustomer?: boolean;
  isSephoraCustomer?: boolean;
  isAccountOverview?: boolean;
  isVerified?: boolean;
}

const BeautyInsider = ({ isFederatedLogin, isZapposCustomer, isSephoraCustomer, isVerified, isAccountOverview }: BeautyInsiderProps) => {
  const {
    testId,
    marketplace: { hasFederatedLogin }
  } = useMartyContext();
  const { view, setView } = useSephoraContext();
  const isLoading = useSelector(selectIsLoadingLoyalty);
  const linkedEmail = useSelector(selectLoyaltyEmail);
  const returnTo = useSelector(selectPathname);
  const dispatch = useDispatch();

  const isFormView = view === 'linkEmail' || view === 'registerAccount' || view === 'verifyEmail';

  const handleSignIn = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (hasFederatedLogin) {
        e.preventDefault();
        dispatch(setFederatedLoginModalVisibility(true, { sourcePageType: SEPHORA_PAGE_TYPE, returnTo }));
        track(() => [evSephoraBeautyInsider, { interaction: SephoraInteraction.SIGN_IN_BUTTON_CLICK }]);
      }
    },
    [hasFederatedLogin]
  );

  useEffect(() => {
    if (isSephoraCustomer) {
      if (isVerified) {
        setView('accountInfo');
      } else {
        setView('verifyEmail');
      }
    }
  }, [isSephoraCustomer]);

  if (isFederatedLogin) {
    return (
      <div className={cn(css.beautyInsider, css.loginContainer)}>
        <div className={css.contentContainer}>
          <SephoraBeautyInsiderIcon className={css.sephoraBILogo} />
          <div className={css.textContent}>
            <p className={css.copy}>
              After signing into your Zappos account, you will be able to sign up for or link your Sephora Beauty Insider account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isZapposCustomer) {
    if (isAccountOverview) return null;

    return (
      <div className={css.beautyInsider} data-test-id={testId('beautyInsider')}>
        <div className={css.contentContainer}>
          <SephoraBeautyInsiderIcon className={css.sephoraBILogo} />
          <div className={cn(css.textContent, css.connectContent)}>
            <p className={css.copy}>
              Earn <strong>1 Beauty Insider point for every $1</strong> spent on all Sephora purchases.&nbsp;
              <MartyLink className={css.linkButton} to={SEPHORA_LEARN_MORE_LINK} target="blank" data-test-id={testId('learnMoreBILink')}>
                Learn More
              </MartyLink>
            </p>
            <p className={css.copy}>Sign in to Zappos for more information.</p>
          </div>
        </div>
        <div className={css.inputContainer}>
          <button className={css.button} type="button" onClick={handleSignIn} data-test-id={testId('zapposSignInBI')}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (isFormView) {
    return (
      <div className={cn(css.beautyInsider, { [css.accountContainer]: isAccountOverview })} data-test-id={testId('beautyInsider')}>
        <div className={css.contentContainer}>
          <SephoraBeautyInsiderIcon className={css.sephoraBILogo} />
          <div className={cn(css.textContent, css.connectContent)}>
            <BeautyInsiderFormHeader />
          </div>
        </div>
        <div className={cn(css.inputContainer, css.formContainer)}>
          <BeautyInsiderForm />
        </div>
      </div>
    );
  }

  return isLoading ? (
    <SmallLoader />
  ) : (
    <div className={cn(css.beautyInsider, { [css.accountContainer]: isAccountOverview })} data-test-id={testId('beautyInsider')}>
      <div className={cn(css.contentContainer, { [css.sephoraContent]: isSephoraCustomer })}>
        {isSephoraCustomer && isVerified && !isAccountOverview ? (
          <SephoraUserIcon className={css.sephoraUserLogo} />
        ) : (
          <SephoraBeautyInsiderIcon className={css.sephoraBILogo} />
        )}
        <div className={cn(css.textContent, css.connectContent)}>
          {!isSephoraCustomer && (
            <p className={css.copy}>
              Earn <strong>1 Beauty Insider point for every $1</strong> spent on all Sephora purchases.&nbsp;
              <MartyLink className={css.linkButton} to={SEPHORA_LEARN_MORE_LINK} target="blank">
                Learn More
              </MartyLink>
            </p>
          )}
          {isSephoraCustomer && !isAccountOverview && (
            <p className={css.copy} data-test-id={testId('beautyInsiderLinkedAccount')}>
              Thank you for being a Beauty Insider! Your account is linked to Sephora. You will earn Beauty Insider points for Sephora
              products.&nbsp;
              <Link className={css.linkButton} to="/account">
                Manage your account
              </Link>
            </p>
          )}
          {isSephoraCustomer && isAccountOverview && (
            <>
              <p className={css.copy}>Account: {linkedEmail}</p>
              <p className={css.copy}>
                You earn <strong>1 point for every $1</strong> spent on Sephora beauty.&nbsp;
              </p>
              <button
                className={css.linkButton}
                type="button"
                onClick={() => {
                  track(() => [evSephoraBeautyInsider, { interaction: SephoraInteraction.UNLINK_ACCOUNT_BUTTON_CLICK }]);
                  dispatch(unlinkSephoraAccount());
                }}
              >
                Unlink Account
              </button>
            </>
          )}
          {!isSephoraCustomer && (
            <p className={css.copy}>
              Already a member?{' '}
              <button
                className={css.linkButton}
                type="button"
                onClick={() => {
                  track(() => [evSephoraBeautyInsider, { interaction: SephoraInteraction.LINK_ACCOUNT_BUTTON_CLICK }]);
                  setView('linkEmail');
                }}
                data-test-id={testId('linkBeautyInsiderAccount')}
              >
                Link your account
              </button>
            </p>
          )}
        </div>
      </div>
      <div className={css.inputContainer}>
        {!isSephoraCustomer && (
          <button
            className={css.button}
            type="button"
            onClick={() => {
              track(() => [evSephoraBeautyInsider, { interaction: SephoraInteraction.JOIN_BUTTON_CLICK }]);
              setView('linkEmail');
            }}
            data-test-id={testId('joinBeautyInsiderButton')}
          >
            Join Now
          </button>
        )}
      </div>
    </div>
  );
};

export default BeautyInsider;
